import type { ColorScale, Rgb } from './colorScale';

/** Entries when the ramp is continuous, where there is no boundary to displace. */
export const RAMP_LOOKUP_SIZE = 256;

/**
 * Entries for one scale.
 *
 * One per band when banded, so a boundary falls exactly where the scale says it does. A fixed 256
 * does not divide by nine - the persisted default - which puts the colour change up to half an
 * entry from the edge, and on the contour chart the band edge is the isoline, so the line ends up
 * drawn where the colour does not change. {@link toRampTextureWidth} solves the same problem for
 * the GPU, where the table also has to be wide enough for a sampler.
 */
export const toRampLookupSize = ({ bands }: ColorScale): number =>
  bands > 0 ? bands : RAMP_LOOKUP_SIZE;

export type RampLookup = {
  /**
   * The nearest entry, which is exactly `colorScale.forValue` for a banded scale.
   *
   * Returns the table's own array, so callers must not mutate it.
   */
  nearest: (value: number) => Rgb;
  /**
   * Writes the colour into the caller's array rather than returning one.
   *
   * Blended between entries when the scale is continuous and stepped when it is banded - which is
   * what a GPU sampler does with `linear` and `nearest`, so the two renderers agree by
   * construction. At a million covered pixels a frame the allocation is the cost, not the
   * arithmetic, which is why this writes rather than returns.
   */
  into: (value: number, out: [number, number, number]) => void;
  size: number;
};

/**
 * Bakes a scale into a table indexed per pixel.
 *
 * `colorScale.forValue` costs nine `Math.pow` calls and an allocation, and both renderers called it
 * once per device pixel: 587 ms for a 1200x900 contour pane at device pixel ratio 2. The table has
 * at most `bands` distinct outputs, so evaluating it more than that many times per frame is work
 * with no result.
 */
export const createRampLookup = (colorScale: ColorScale): RampLookup => {
  const size = toRampLookupSize(colorScale);
  const table = Array.from({ length: size }, (_, index) =>
    colorScale.at((index + 0.5) / size)
  );
  const [min, max] = colorScale.range;
  const span = max - min || 1;
  const banded = colorScale.bands > 0;

  const normalise = (value: number): number =>
    Math.min(1, Math.max(0, (value - min) / span));

  const nearest = (value: number): Rgb =>
    table[Math.min(size - 1, Math.floor(normalise(value) * size))];

  const into = (value: number, out: [number, number, number]): void => {
    if (banded) {
      const [r, g, b] = nearest(value);
      out[0] = r;
      out[1] = g;
      out[2] = b;
      return;
    }

    const position = normalise(value) * size - 0.5;
    const lower = Math.min(size - 1, Math.max(0, Math.floor(position)));
    const upper = Math.min(size - 1, lower + 1);
    const between = Math.min(1, Math.max(0, position - lower));

    for (let channel = 0; channel < 3; channel++) {
      out[channel] =
        table[lower][channel] +
        (table[upper][channel] - table[lower][channel]) * between;
    }
  };

  return { nearest, into, size };
};
